"use client"

import * as React from "react"
import { format, isValid } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { enUS, fr, nlBE } from "date-fns/locale"
import DateInput from "./date-input"
import { Matcher } from "react-day-picker"

interface DatePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  onDateChange?: (value:Date | undefined) => void;
  locale?: 'nlBE' | 'enUS' | 'fr';
  value?: Date;
  disabled?: boolean;
  calendarDisabled?: Matcher | Matcher[];
}

export const DatePicker : React.FC<DatePickerProps> = ({placeholder="Pick a date", onDateChange, locale="nlBE", value, className, id, disabled=false, calendarDisabled}) => {
  const [date, setDate] = React.useState<Date>()

  React.useEffect(() => {
    if(value){
      setDate(value)
    }
  }, [value])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          id={id}
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground", className
          )}
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          disabled={calendarDisabled}
          mode="single"
          selected={date}
          onSelect={(date) => { setDate(date); onDateChange && onDateChange(date);  }}
          initialFocus
          locale={locale === 'nlBE' ? nlBE : locale === 'fr' ? fr : locale === 'enUS' ? enUS : nlBE}
        />
      </PopoverContent>
    </Popover>
  )
}

export const DatePickerInput : React.FC<DatePickerProps> = ({onDateChange, locale="nlBE", value, className, id, disabled=false, calendarDisabled}) => {
  const [date, setDate] = React.useState<Date | undefined>()
  const tmpDate = React.useRef<Date | undefined>()

  React.useEffect(() => {
    if(value){
      setDate(value)
    }
  }, [value])

  return (
    <Popover>
      <div className={cn('border rounded-md flex items-center h-10 border-input bg-background px-3 py-2 w-full', className)}>
        <DateInput disabled={disabled} value={date} onBlur={()=>{ setDate(tmpDate.current) }} onValueChange={(d)=>{ tmpDate.current = d }} className='w-full text-sm focus:outline-none disabled:bg-transparent bg:opacity-50'/>
        
        <PopoverTrigger asChild><button disabled={disabled} className='h-8 w-8 min-w-[32px] flex justify-center items-center hover:bg-accent rounded-full translate-x-2 text-neutral-500' id={id}><CalendarIcon className='w-4 h-4' strokeWidth={1.5}/></button></PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0">
        <Calendar
          disabled={calendarDisabled}
          month={isValid(date) ? date : new Date}
          mode="single"
          selected={date}
          onSelect={(date) => { setDate(date); onDateChange && onDateChange(date);  }}
          initialFocus
          locale={locale === 'nlBE' ? nlBE : locale === 'fr' ? fr : locale === 'enUS' ? enUS : nlBE}
        />
      </PopoverContent>
    </Popover>
  )
}